import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Banknote, MessageCircle, CheckCircle2 } from "lucide-react";
import { api, rupee, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [method, setMethod] = useState("cod");
  const [config, setConfig] = useState({ dummy_payment: true, payment_methods: { online: true, cod: true, whatsapp: true } });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", address: "" });

  const methods = { online: true, cod: true, whatsapp: true, ...(config.payment_methods || {}) };
  const onlineOn = methods.online;
  const codOn = methods.cod;
  const whatsappOn = methods.whatsapp;
  const payOptions = [
    onlineOn && {
      id: "online", icon: CreditCard, title: "Pay Online (Razorpay)",
      sub: config.dummy_payment ? "UPI / Card / Netbanking — running in TEST mode (no real charge)" : "UPI / Card / Netbanking",
      testid: "pay-online",
    },
    codOn && { id: "cod", icon: Banknote, title: "Cash on Delivery", sub: "Pay when your order arrives", testid: "pay-cod" },
    whatsappOn && { id: "whatsapp", icon: MessageCircle, title: "Order on WhatsApp", sub: "Send your order details to us directly", testid: "pay-whatsapp" },
  ].filter(Boolean);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user) setForm((f) => ({ ...f, name: user.name, email: user.email, phone: user.phone || f.phone })); }, [user]);
  useEffect(() => {
    const ids = [];
    if (onlineOn) ids.push("online");
    if (codOn) ids.push("cod");
    if (whatsappOn) ids.push("whatsapp");
    if (!ids.length) return;
    if (!ids.includes(method)) setMethod(ids[0]);
  }, [onlineOn, codOn, whatsappOn, method]);

  if (items.length === 0 && !done)
    return <div className="max-w-2xl mx-auto px-6 py-24 text-center text-muted-foreground">Your bag is empty. <a href="/shop" className="text-primary font-semibold">Shop now</a></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async () => {
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    if (method !== "whatsapp" && !form.address) return toast.error("Please enter a delivery address");
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout", {
        items, customer: form, payment_method: method,
      });
      clear();
      if (method === "whatsapp" && data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
      setDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (done)
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
        <h1 className="font-display text-4xl font-light mt-6">Order placed!</h1>
        <p className="text-muted-foreground mt-2">Order <span className="font-mono font-semibold text-foreground">{done.order_number}</span></p>
        <p className="text-sm text-muted-foreground mt-1">{done.payment_status === "paid" ? "Payment received (TEST MODE)" : done.payment_status === "cod_pending" ? "Cash on Delivery — we'll confirm shortly" : "We'll be in touch on WhatsApp"}</p>
        <div className="mt-3 font-mono text-xl font-bold">{rupee(done.total)}</div>
        <div className="flex gap-3 justify-center mt-8">
          <button onClick={() => nav("/account")} className="border border-foreground text-foreground px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-[hsl(var(--ivory))] transition-colors" data-testid="view-orders-btn">View Orders</button>
          <button onClick={() => nav("/shop")} className="bg-primary text-primary-foreground px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-foreground transition-colors" data-testid="continue-shopping-btn">Continue Shopping</button>
        </div>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">
          {/* Details */}
          <section className="bg-white rounded-2xl border border-border p-6">
            <h2 className="font-semibold text-lg mb-4">Delivery Details {!user && <span className="text-xs font-normal text-muted-foreground">(guest checkout)</span>}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name*" value={form.name} onChange={set("name")} testid="co-name" />
              <Field label="Phone*" value={form.phone} onChange={set("phone")} testid="co-phone" />
              <Field label="Email" value={form.email} onChange={set("email")} testid="co-email" />
              <div className="sm:col-span-2">
                <label className="text-sm font-medium">Address</label>
                <textarea value={form.address} onChange={set("address")} rows={3} data-testid="co-address"
                  className="mt-1.5 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white rounded-2xl border border-border p-6">
            <h2 className="font-semibold text-lg mb-4">Payment Method</h2>
            <div className="space-y-3">
              {payOptions.map((opt) => (
                <PayOption key={opt.id} id={opt.id} active={method} onSelect={setMethod} icon={opt.icon} title={opt.title} sub={opt.sub} testid={opt.testid} />
              ))}
              {payOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">No payment methods are available right now. Please contact the store.</p>
              )}
            </div>
          </section>
        </div>

        <div className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-border p-6">
          <h2 className="font-semibold text-lg mb-4">Order Summary</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((it, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <img src={it.image} alt="" className="w-12 h-14 rounded-lg object-cover bg-secondary" />
                <div className="flex-1">
                  <p className="font-medium line-clamp-1">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{[it.size, it.color].filter(Boolean).join(" · ")} × {it.qty}</p>
                </div>
                <span className="font-mono">{rupee(it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border my-4" />
          <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono text-lg">{rupee(subtotal)}</span></div>
          <button onClick={placeOrder} disabled={placing || !payOptions.length} className="w-full mt-6 bg-foreground text-[hsl(var(--ivory))] py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors disabled:opacity-60" data-testid="place-order-btn">
            {placing ? "Placing…" : method === "whatsapp" ? "Send on WhatsApp" : method === "cod" ? "Place COD Order" : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <input value={value} onChange={onChange} data-testid={testid} className="mt-1.5 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
  </div>
);

const PayOption = ({ id, active, onSelect, icon: Icon, title, sub, testid }) => (
  <button onClick={() => onSelect(id)} data-testid={testid}
    className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-colors ${active === id ? "border-primary bg-secondary" : "border-border hover:border-primary/40"}`}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active === id ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}><Icon className="w-5 h-5" /></div>
    <div className="flex-1">
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
    <span className={`w-4 h-4 rounded-full border-2 ${active === id ? "border-primary bg-primary" : "border-input"}`} />
  </button>
);
